import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY     = '#1c2266';
const PRIMARY_MID = '#2d35a0';
const PRIMARY_LT  = '#eef0ff';

const fmtV    = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const TaxRegisterSale = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'GSTReportApi',
                `/SaleRegistrationGSTDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch tax register sale report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.BillNumber    || '').toLowerCase().includes(q) ||
            (r.CustomerName  || '').toLowerCase().includes(q) ||
            (r.GSTNumber     || '').toLowerCase().includes(q) ||
            (r.SupplyType    || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        TaxableAmount: acc.TaxableAmount + (parseFloat(r.TaxableAmount) || 0),
        CGSTAmount:    acc.CGSTAmount    + (parseFloat(r.CGSTAmount)    || 0),
        SGSTAmount:    acc.SGSTAmount    + (parseFloat(r.SGSTAmount)    || 0),
        IGSTAmount:    acc.IGSTAmount    + (parseFloat(r.IGSTAmount)    || 0),
        TotalAmount:   acc.TotalAmount   + (parseFloat(r.TotalAmount)   || 0),
    }), { TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'TaxRegisterSale', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Date', 'Bill No', 'Customer', 'GST No', 'Supply Type', 'GST Rate %', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'];
        const rows = filtered.map((r, i) => [
            i + 1, fmtDate(r.BillDate), r.BillNumber, r.CustomerName,
            r.GSTNumber || '-', r.SupplyType || '-', r.GSTRate || 0,
            r.TaxableAmount, r.CGSTAmount, r.SGSTAmount, r.IGSTAmount, r.TotalAmount,
        ]);

        await exportStyledExcel({
            reportTitle: 'TAX REGISTER SALE REPORT',
            sheetName: 'Tax Register Sale',
            fileName: `TaxRegisterSale_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', '', '', '', 'Totals',
                fmtV(totals.TaxableAmount), fmtV(totals.CGSTAmount),
                fmtV(totals.SGSTAmount), fmtV(totals.IGSTAmount), fmtV(totals.TotalAmount)],
            columnWidths: [4, 12, 14, 22, 16, 12, 10, 14, 12, 12, 12, 14],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading tax register sale..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Tax Register Sale
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>GST Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input placeholder="Search bill / customer / GST..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 200, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Bills',     value: filtered.length,               color: PRIMARY },
                            { label: 'Taxable',   value: `₹${fmtV(totals.TaxableAmount)}`, color: '#374151' },
                            { label: 'CGST',      value: `₹${fmtV(totals.CGSTAmount)}`,    color: '#0277bd' },
                            { label: 'SGST',      value: `₹${fmtV(totals.SGSTAmount)}`,    color: '#0277bd' },
                            { label: 'IGST',      value: `₹${fmtV(totals.IGSTAmount)}`,    color: '#7c3aed' },
                            { label: 'Total',     value: `₹${fmtV(totals.TotalAmount)}`,   color: '#059669' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} records
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1000, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 90 }}>Date</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 110 }}>Bill No</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Customer</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 120 }}>GST No</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 90 }}>Supply Type</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 70 }}>GST %</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100 }}>Taxable</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>CGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>SGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>IGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100, background: '#1a1f6e' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const prev = filtered[i - 1];
                                const isDup = prev &&
                                    String(r.BillDate).split('T')[0] === String(prev.BillDate).split('T')[0] &&
                                    r.BillNumber   === prev.BillNumber &&
                                    r.CustomerName === prev.CustomerName &&
                                    (r.GSTNumber   || '') === (prev.GSTNumber || '');
                                return (
                                <tr key={`${r.BillNumber}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                    <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{isDup ? '' : i + 1}</td>
                                    <td style={{ ...tdBase, color: '#374151' }}>{isDup ? '' : fmtDate(r.BillDate)}</td>
                                    <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{isDup ? '' : (r.BillNumber || '-')}</td>
                                    <td style={{ ...tdBase, color: '#1e293b' }}>{isDup ? '' : (r.CustomerName || '-')}</td>
                                    <td style={{ ...tdBase, color: '#64748b', fontSize: 10 }}>{isDup ? '' : (r.GSTNumber || '-')}</td>
                                    <td style={{ ...tdBase }}>
                                        {r.SupplyType ? (
                                            <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                                                background: r.SupplyType === 'Inter State' ? '#ede9fe' : '#e0f2fe',
                                                color: r.SupplyType === 'Inter State' ? '#7c3aed' : '#0277bd' }}>
                                                {r.SupplyType}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#f59e0b', fontWeight: 600 }}>{r.GSTRate ? `${r.GSTRate}%` : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#374151', fontWeight: 600 }}>{fmtV(r.TaxableAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{parseFloat(r.CGSTAmount) ? fmtV(r.CGSTAmount) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{parseFloat(r.SGSTAmount) ? fmtV(r.SGSTAmount) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#7c3aed' }}>{parseFloat(r.IGSTAmount) ? fmtV(r.IGSTAmount) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(r.TotalAmount)}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={7} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#374151' }}>{fmtV(totals.TaxableAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.CGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.SGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{fmtV(totals.IGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(totals.TotalAmount)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TaxRegisterSale;
